import React, { useState, useEffect, useCallback } from 'react';
import { Home, Users, CreditCard } from 'lucide-react';
import { householdAPI, paymentAccountAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { getAccountTypeIcon } from '../../utils/formatters';

/**
 * HouseholdOverview - Overview tab showing household summary
 */
function HouseholdOverview({ household }) {
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { error: showError } = useToast();

  const loadOverviewData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersResponse, accountsResponse] = await Promise.all([
        householdAPI.getMembers(),
        paymentAccountAPI.getAll(false)
      ]);
      setMembers(membersResponse.data);
      setAccounts(accountsResponse.data);
    } catch (error) {
      console.error('Failed to load overview data:', error);
      showError('Failed to load household overview');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadOverviewData();
  }, [loadOverviewData]);

  const getHouseholdSettings = () => {
    if (!household.settings) return {};
    return typeof household.settings === 'string'
      ? JSON.parse(household.settings)
      : household.settings;
  };

  const settings = getHouseholdSettings();
  const activeMembers = members.filter(m => m.is_active);
  const managers = members.filter(m => m.household_role === 'manager');

  if (loading) {
    return (
      <div className="household-section">
        <h2>Household Overview</h2>
        <div className="loading-state">Loading household overview...</div>
      </div>
    );
  }

  return (
    <div className="household-section">
      <h2>{household.name}</h2>
      <p className="section-description">Overview of your household information</p>

      <div className="overview-grid">
        {/* Household Info Card */}
        <div className="overview-card">
          <div className="overview-card-header">
            <Home size={24} className="overview-icon" />
            <h3>Household Information</h3>
          </div>
          <div className="overview-card-body">
            <div className="overview-stat">
              <span className="stat-label">Name</span>
              <span className="stat-value">{household.name}</span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Created</span>
              <span className="stat-value">{new Date(household.created_at).toLocaleDateString()}</span>
            </div>
            {settings.currency && (
              <div className="overview-stat">
                <span className="stat-label">Currency</span>
                <span className="stat-value">{settings.currency}</span>
              </div>
            )}
            {settings.dateFormat && (
              <div className="overview-stat">
                <span className="stat-label">Date Format</span>
                <span className="stat-value">{settings.dateFormat}</span>
              </div>
            )}
          </div>
        </div>

        {/* Members Card */}
        <div className="overview-card">
          <div className="overview-card-header">
            <Users size={24} className="overview-icon" />
            <h3>Members</h3>
          </div>
          <div className="overview-card-body">
            <div className="overview-stat-large">
              <span className="stat-number">{members.length}</span>
              <span className="stat-label">Total Members</span>
            </div>
            <div className="overview-stats-row">
              <div className="overview-stat-small">
                <span className="stat-value">{activeMembers.length}</span>
                <span className="stat-label">Active</span>
              </div>
              <div className="overview-stat-small">
                <span className="stat-value">{managers.length}</span>
                <span className="stat-label">Managers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Accounts Card */}
        <div className="overview-card">
          <div className="overview-card-header">
            <CreditCard size={24} className="overview-icon" />
            <h3>Payment Accounts</h3>
          </div>
          <div className="overview-card-body">
            <div className="overview-stat-large">
              <span className="stat-number">{accounts.length}</span>
              <span className="stat-label">Active Accounts</span>
            </div>
            {accounts.length > 0 && (
              <div className="account-list-preview">
                {accounts.slice(0, 3).map(account => (
                  <div key={account.id} className="account-preview-item">
                    {account.card_image ? (
                      <img 
                        src={account.card_image} 
                        alt="" 
                        className="account-preview-card-image"
                      />
                    ) : (
                      <span className="account-preview-icon">
                        {getAccountTypeIcon(account.account_type)}
                      </span>
                    )}
                    <span className="account-preview-name">
                      {account.account_name}
                      {account.last_four && ` (${account.last_four})`}
                    </span>
                  </div>
                ))}
                {accounts.length > 3 && (
                  <div className="account-preview-item more">
                    <span className="account-preview-more">+{accounts.length - 3} more</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
}

export default HouseholdOverview;
